import type { Mode, MoneyRange, PlanStop, RangeInput, StopInput } from '@trippath/shared';

/** A range in cents, as a stop is saved with it. */
export const cents = (r: MoneyRange): RangeInput => ({ min: r.min.minor, max: r.max.minor });

/** What can change of a stop from where it is shown: its days, its costs, its way on. */
export interface StopChange {
  days?: number;
  accommodation?: RangeInput;
  food?: RangeInput;
  local?: RangeInput;
  activities?: RangeInput;
  /** How it goes on, chosen: with `way`, its price. */
  mode?: Mode;
  /** The way on's price and its times, and for a variant the ticket in mind. */
  way?: {
    estimate: RangeInput;
    departureAt: string | null;
    arrivalAt: string | null;
    link: string | null;
    name?: string | null;
  };
}

/**
 * A stop as it is, to save again with what changed: its costs, its way on with its link and
 * times, and its point — none for one only placed by its airport, which stays so.
 */
export function stopInputOf(stop: PlanStop, change: StopChange = {}): StopInput {
  const { place, out } = stop;
  const way = change.way;
  return {
    city: stop.city,
    country: stop.country,
    days: change.days ?? stop.days,
    accommodation: change.accommodation ?? cents(stop.accommodation),
    food: change.food ?? cents(stop.food),
    local: change.local ?? cents(stop.local),
    activities: change.activities ?? cents(stop.activities),
    out:
      change.mode && way
        ? { mode: change.mode, ...way }
        : out && {
            mode: out.mode,
            estimate: way ? way.estimate : cents(out.estimate),
            link: way ? way.link : out.link,
            departureAt: way ? way.departureAt : out.departureAt,
            arrivalAt: way ? way.arrivalAt : out.arrivalAt,
          },
    // Its way's times while how it goes is not chosen: kept, not forgotten by a change.
    wayTimes: stop.wayTimes,
    unresolved: stop.unresolved,
    place:
      place === null || place.guessed
        ? null
        : {
            lat: place.lat,
            lon: place.lon,
            ...(place.name === null ? {} : { name: place.name }),
          },
  };
}

/** A stop as it is but its way on's times: leaving later, the days before the next unplanned. */
export function withWayAt(
  stop: PlanStop,
  departureAt: string | null,
  arrivalAt: string | null,
): StopInput {
  const input = stopInputOf(stop);
  // How it goes not chosen yet, its times are kept on their own.
  return input.out
    ? { ...input, out: { ...input.out, departureAt, arrivalAt } }
    : { ...input, wayTimes: departureAt || arrivalAt ? { departureAt, arrivalAt } : null };
}

/** A stop as it is but its days. */
export function withDays(stop: PlanStop, days: number): StopInput {
  return stopInputOf(stop, { days });
}
